import axios from 'axios';
import qs from 'qs';
import { captureException } from '@sentry/nextjs';
import { getRequestUrlHelper } from '~/src/helpers/get-request-url.helper';
import {
    apiKey,
    apiKeyCmsArticlesAttrCode,
    apiKeyCmsArticlesDictionary,
    apiKeyCmsSDirectoryRecords,
    apiKeyCmsSectionsDictionary,
    defaultUrlCmsArticlesAttrCode,
    defaultUrlCmsArticlesDictionary,
    defaultUrlCmsDirectoryRecords,
    defaultUrlCmsSectionsDictionary,
    buildRelatedArticleUrl,
} from '~/src/repositories/cms/api-urls';
import {
    getMappedLeadData,
    getMappedProductGroup,
    getPermutationLeadData,
} from '~/src/repositories/cms/shared/utils/cms.utils';
import { API_HOST_CMS_ENV } from '~/src/params';
import { CmsCategoryCodeEnum, CmsDirectoryCodeEnum, CmsProductSefUrlEnum } from '~/src/models/enums/cms.enum';
import type { CmsArticleAttributeInterface } from '~/src/repositories/cms/types/cms.types';
import type { DictionaryInterface, SectionsDictionaryProps } from '~/src/repositories/cms/types/cms.sections.types';
import type {
    DirectoryRecordsInterface,
    DirectoryRecordsProps,
} from '~/src/repositories/cms/types/cms.directory.types';
import type {
    ArticlesAttrCodeProps,
    ArticlesDictionaryProps,
    CmsMappedArticlesAllTypes,
    CmsMappedArticlesInterface,
    RelatedArticleInterface,
} from '~/src/repositories/cms/types/cms.articles.types';

export const getSectionsDictionary = async <T = CmsArticleAttributeInterface>({
    isSSR,
    categoryCode,
    productCode = CmsProductSefUrlEnum.MTS_AGENTS,
    showArticleContent = true,
}: SectionsDictionaryProps): Promise<DictionaryInterface<T>[] | []> => {
    const url = getRequestUrlHelper({
        isSSR,
        apiKey: apiKeyCmsSectionsDictionary,
        defaultUrl: defaultUrlCmsSectionsDictionary,
        apiHost: API_HOST_CMS_ENV,
    });

    const config = {
        params: {
            productCode,
            categoryCode,
            showArticleContent,
        },
    };

    try {
        const { data } = await axios.get<DictionaryInterface<T>[]>(url, config);
        return data;
    } catch (error: unknown) {
        // eslint-disable-next-line no-console
        console.log(error);
        captureException(error);
        return [];
    }
};

export const getDirectoryRecordsDictionary = async ({
    isSSR,
    directoryCode,
    productCode = CmsProductSefUrlEnum.MTS_AGENTS,
}: DirectoryRecordsProps): Promise<DirectoryRecordsInterface[] | []> => {
    const url = getRequestUrlHelper({
        isSSR,
        apiKey: apiKeyCmsSDirectoryRecords,
        defaultUrl: defaultUrlCmsDirectoryRecords,
        apiHost: API_HOST_CMS_ENV,
    });

    const config = {
        params: {
            productCode,
            directoryCode,
        },
    };

    try {
        const { data } = await axios.get<DirectoryRecordsInterface[]>(url, config);
        return data;
    } catch (error: unknown) {
        // eslint-disable-next-line no-console
        console.log(error);
        captureException(error);
        return [];
    }
};

export const getArticlesDictionary = async ({
    isSSR,
    categoryCode,
    productSefUrl = CmsProductSefUrlEnum.MTS_AGENTS,
    articleSefUrl,
}: ArticlesDictionaryProps): Promise<CmsMappedArticlesAllTypes> => {
    const url = getRequestUrlHelper({
        isSSR,
        apiKey: apiKeyCmsArticlesDictionary,
        defaultUrl: defaultUrlCmsArticlesDictionary,
        apiHost: API_HOST_CMS_ENV,
    });

    const config = {
        params: {
            productSefUrl,
            articleSefUrl,
            categoryCode,
        },
    };

    try {
        const { data } = await axios.get<CmsMappedArticlesInterface>(url, config);
        return data;
    } catch (error: unknown) {
        // eslint-disable-next-line no-console
        console.log(error);
        captureException(error);
        return { cmsFailed: true };
    }
};

export const getArticlesAttrCode = async ({
    isSSR,
    categoriesCode,
    productCode = CmsProductSefUrlEnum.MTS_AGENTS,
    attrCode,
}: ArticlesAttrCodeProps): Promise<CmsMappedArticlesAllTypes> => {
    const url = getRequestUrlHelper({
        isSSR,
        apiKey: apiKeyCmsArticlesAttrCode,
        defaultUrl: defaultUrlCmsArticlesAttrCode,
        apiHost: API_HOST_CMS_ENV,
    });

    const config = {
        params: {
            productCode,
            categoriesCode,
            attrCode,
        },
        paramsSerializer: (params: unknown) => qs.stringify(params, { encode: false }),
    };

    try {
        const { data } = await axios.get<CmsMappedArticlesInterface>(url, config);
        return data;
    } catch (error: unknown) {
        // eslint-disable-next-line no-console
        console.log(error);
        captureException(error);
        return { cmsFailed: true };
    }
};

export const getRelatedArticles = async ({
    articleId,
    isSSR,
}: RelatedArticleInterface): Promise<CmsMappedArticlesAllTypes> => {
    const url = getRequestUrlHelper({
        isSSR: isSSR,
        apiKey: buildRelatedArticleUrl(articleId, apiKey),
        defaultUrl: buildRelatedArticleUrl(articleId),
        apiHost: API_HOST_CMS_ENV,
    });

    const config = {
        params: {
            articleId,
        },
    };

    try {
        const { data } = await axios.get<CmsMappedArticlesInterface>(url, config);
        return data;
    } catch (error: unknown) {
        // eslint-disable-next-line no-console
        console.log(error);
        captureException(error);
        return { cmsFailed: true };
    }
};

export const getMtsProductsForLead = async (isSSR?: boolean) => {
    const data = await getSectionsDictionary({ isSSR, categoryCode: CmsCategoryCodeEnum.LEAD_PRODUCTS });
    const productGroups = await getDirectoryRecordsDictionary({
        isSSR,
        directoryCode: CmsDirectoryCodeEnum.PRODUCT_GROUP,
    });
    const productGroupsArray = getMappedProductGroup(productGroups);
    const mappedLeadData = getMappedLeadData(data);

    return getPermutationLeadData(mappedLeadData, productGroupsArray);
};
